import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, Card, Button, Input, Space, Switch, Tag, message, 
  Dropdown, Menu, Modal, Tooltip, Spin, Typography, Divider,
  Row, Col, Form, Select, TreeSelect, Pagination, Drawer, Avatar, Descriptions, Tabs, Empty,
  Alert
} from 'antd';
import { 
  SearchOutlined, SyncOutlined, DownOutlined, UserOutlined,
  PhoneOutlined, MailOutlined, GlobalOutlined, TeamOutlined,
  EnvironmentOutlined, InfoCircleOutlined, IdcardOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import api from '../services/api';
import { useAuth } from '../utils/auth';

const { Title, Text, Paragraph } = Typography;

const WeworkEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [syncStatus, setSyncStatus] = useState({
    lastSyncTime: null,
    status: null,
    message: ''
  });
  // 试用状态摘要
  const [trialInfo, setTrialInfo] = useState(null);
  // 抽屉状态
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerEmployee, setDrawerEmployee] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const { user, tenant } = useAuth();

  // 内联许可统计（用于试用提示展示）
  const [licenseSummaryInline, setLicenseSummaryInline] = useState(null);
  // 同步频控
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // 加载成员数据
  const loadEmployees = useCallback(async (page = 0, size = 10) => {
    try {
      setLoading(true);
      
      // 如果选择了部门，使用递归查询接口
      if (selectedDepartment) {
        const response = await api.get(`/wework/employees/by-department/${selectedDepartment}`, {
          params: {
            weworkAccountId: tenant?.weworkCorpId
          }
        });
        console.log('获取到的部门员工数据:', response.data);
        
        let employees = response.data.data || [];
        
        // 如果有关键词搜索，在结果中过滤
        if (searchKeyword) {
          employees = employees.filter(employee => 
            (employee.name && employee.name.includes(searchKeyword)) ||
            (employee.userid && employee.userid.includes(searchKeyword)) ||
            (employee.alias && employee.alias.includes(searchKeyword))
          );
        }
        
        const totalElements = employees.length;
        
        // 手动分页
        const start = page * size;
        const end = Math.min(start + size, employees.length);
        const paginatedEmployees = start < employees.length ? employees.slice(start, end) : [];
        
        setEmployees(paginatedEmployees);
        setTotalElements(totalElements);
        setPageSize(size);
        setCurrentPage(page);
      } else {
        // 没有选择部门，使用普通查询
        const params = {
          page,
          size,
        };
        
        // 添加关键词搜索
        if (searchKeyword) {
          params.keyword = searchKeyword;
        }
        
        // 添加企业ID筛选
        if (tenant?.weworkCorpId) {
          params.weworkAccountId = tenant.weworkCorpId;
        }
        
        const response = await api.get('/wework/employees', { params });
        console.log('获取到的员工数据:', response.data);
        
        setEmployees(response.data.content || []);
        setTotalElements(response.data.totalElements || 0);
        setPageSize(response.data.size || 10);
        setCurrentPage(response.data.number || 0);
      }
    } catch (error) {
      console.error('加载企业微信成员失败:', error);
      message.error('加载企业微信成员失败');
    } finally {
      setLoading(false);
    }
  }, [searchKeyword, selectedDepartment, tenant?.weworkCorpId]);

  // 加载部门数据
  const loadDepartments = async () => {
    try {
      // 使用树形接口获取带层级的部门数据
      const response = await api.get('/wework/departments/tree');
      console.log('获取到的部门树形数据:', response.data);
      setDepartments(response.data || []);
    } catch (error) {
      console.error('加载企业微信部门失败:', error);
      message.error('加载企业微信部门失败');
    }
  };

  // （已移除许可统计弹窗）

  // 加载内联许可统计（不弹窗）
  const loadLicenseSummaryInline = async () => {
    try {
      const res = await api.get('/wework/licenses/summary', { params: { tenantId: tenant?.id } });
      setLicenseSummaryInline(res.data || {});
    } catch (e) {
      console.error('加载许可统计失败:', e);
    }
  };

  // 加载试用状态摘要
  const loadTrialInfo = async () => {
    try {
      const res = await api.get('/wework/licenses/trialInfo', { params: { tenantId: tenant?.id } });
      setTrialInfo(res.data || null);
    } catch (e) {
      console.error('加载试用状态失败:', e);
    }
  };

  // 同步状态
  const loadSyncStatus = async () => {
    try {
      const response = await api.get('/wework/employees/sync-status');
      setSyncStatus(response.data);
    } catch (error) {
      console.error('加载同步状态失败:', error);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadEmployees(0, pageSize);
    loadDepartments();
    loadSyncStatus();
    loadTrialInfo();
    loadLicenseSummaryInline();
  }, [tenant]);

  // 监听部门筛选变化
  useEffect(() => {
    if (tenant) {  // 确保tenant已加载
      loadEmployees(0, pageSize);
    }
  }, [selectedDepartment, tenant]);

  // 处理搜索
  const handleSearch = () => {
    setCurrentPage(0);
    loadEmployees(0, pageSize);
  };

  // 处理分页变化
  const handlePageChange = (page, size) => {
    console.log('分页变化:', page, size);
    loadEmployees(page - 1, size);
  };

  // 处理每页显示数量变化
  const handlePageSizeChange = (current, size) => {
    setCurrentPage(0);
    setPageSize(size);
    loadEmployees(0, size);
  };

  // 工具：格式化epoch秒
  const formatEpochSeconds = (sec) => {
    if (!sec || Number.isNaN(Number(sec))) return '-';
    const d = new Date(Number(sec) * 1000);
    return d.toLocaleString();
  };

  // 试用状态提示
  const renderTrialAlert = () => {
    if (!trialInfo) return null;
    const licenseStatus = trialInfo.license_status;
    const allow = trialInfo.allow_without_license;
    const start = trialInfo.trial_start_time;
    const end = trialInfo.trial_end_time;
    const checkTime = trialInfo.license_check_time;

    if (licenseStatus === 0) {
      return (
        <Alert
          type="info"
          showIcon
          message="未开启License拦截"
          description={
            <div>
              <div>当前无需接口许可，成员启用后可直接使用系统功能。</div>
              {checkTime ? <div>拦截校验开始时间：{formatEpochSeconds(checkTime)}</div> : null}
            </div>
          }
          style={{ marginBottom: '12px', padding: '10px 24px', border: 'none' }}
        />
      );
    }

    if (allow) {
      const nowSec = Math.floor(Date.now() / 1000);
      const inTrial = start && end && nowSec >= start && nowSec < end;
      const remainDays = inTrial ? Math.ceil((end - nowSec) / 86400) : null;
      return (
        <Alert
          type="success"
          showIcon
          message={inTrial ? `试用期内（剩余 ${remainDays} 天）` : '拦截未开始（无数量限制）'}
          description={
            <div>
              {inTrial ? (
                <>
                  <div>到期时间：{formatEpochSeconds(end)}</div>
                  <div>不限制开启人数。</div>
                </>
              ) : (
                <>
                  {checkTime ? <div>拦截将于：{formatEpochSeconds(checkTime)} 生效</div> : null}
                  <div>当前不限制开启人数。</div>
                </>
              )}
            </div>
          }
          style={{ marginBottom: '12px', padding: '10px 24px', border: 'none' }}
        />
      );
    }

    const used = licenseSummaryInline?.usedExternal;
    const available = licenseSummaryInline?.availableExternal;
    return (
      <Alert
        type="warning"
        showIcon
        message="已开启许可拦截（试用已结束）"
        description={
          <div>
            <div>已用互通：{used ?? '-'} ｜ 可用互通：{available ?? '-'}</div>
            <div>启用成员将自动尝试分配或转移许可，不足则启用失败。</div>
          </div>
        }
        style={{ marginBottom: '12px', padding: '10px 24px', border: 'none' }}
      />
    );
  };

  // 处理同步企业微信成员
  const handleSync = async () => {
    try {
      // 频控：1分钟只允许点击一次
      if (syncing) return;
      const now = Date.now();
      if (lastSyncTime && now - lastSyncTime < 60 * 1000) {
        message.warning('操作过于频繁，请稍后再试（1分钟内仅可同步一次）');
        return;
      }
      setLastSyncTime(now);

      setSyncing(true);

      // 1) 同步企业微信成员
      await api.post('/sync/employees/async', { sourceType: 'WEWORK', syncType: 'EMPLOYEE' });

      // 2) 同步激活码池 与 3) 同步已激活许可（并行，失败不阻断主流程）
      try {
        await Promise.all([
          api.post('/wework/licenses/syncCodePool', null, { params: { months: 12, tenantId: tenant?.id } }),
          api.post('/wework/licenses/syncActive', null, { params: { tenantId: tenant?.id } })
        ]);
      } catch (inner) {
        console.error('同步许可数据失败:', inner);
      }

      message.success('已开始同步企业微信成员并更新许可数据，请稍后刷新查看结果');

      // 刷新内联许可统计
      await loadLicenseSummaryInline();

      // 略等后刷新状态与列表
      setTimeout(() => {
        loadSyncStatus();
        loadEmployees(currentPage, pageSize);
      }, 3000);
    } catch (error) {
      console.error('同步企业微信成员失败:', error);
      message.error('同步企业微信成员失败');
    } finally {
      setSyncing(false);
    }
  };

  // （已合并：同步激活码池、同步已激活许可 -> handleSync）

  // 获取成员详情
  const getEmployeeDetail = async (id) => {
    try {
      setDrawerLoading(true);
      setDrawerVisible(true);
      
      // 先从当前列表中查找
      const employeeInList = employees.find(emp => emp.id === id);
      if (employeeInList) {
        setDrawerEmployee(employeeInList);
        setDrawerLoading(false);
        return;
      }
      
      // 如果列表中没有，则请求API
      const response = await api.get(`/wework/employees/${id}`);
      console.log('获取到的成员详情:', response.data);
      setDrawerEmployee(response.data);
    } catch (error) {
      console.error('获取成员详情失败:', error);
      message.error('获取成员详情失败');
    } finally {
      setDrawerLoading(false);
    }
  };

  // 关闭抽屉
  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setDrawerEmployee(null);
  };

  // 递归查找部门名称
  const getDepartmentName = (deptId) => {
    const findDeptInTree = (depts, targetId) => {
      for (const dept of depts) {
        if (dept.id === targetId) {
          return dept.name;
        }
        if (dept.children && dept.children.length > 0) {
          const found = findDeptInTree(dept.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    
    const name = findDeptInTree(departments, deptId);
    return name || `部门ID:${deptId}`;
  };

  // 转换部门树形数据为TreeSelect需要的格式
  const convertDepartmentsToTreeData = (depts) => {
    return depts.map(dept => ({
      title: dept.name,
      value: dept.id,
      key: dept.id,
      children: dept.children && dept.children.length > 0 
        ? convertDepartmentsToTreeData(dept.children)
        : undefined
    }));
  };

  // 辅助函数：解析日期或时间字符串
  const parseDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return null;
    try {
      // 尝试解析ISO格式日期字符串
      return new Date(dateTimeStr);
    } catch (e) {
      console.error('日期解析失败:', dateTimeStr, e);
      return null;
    }
  };

  // 辅助函数：格式化日期显示
  const formatDateTime = (dateTimeStr) => {
    const date = parseDateTime(dateTimeStr);
    if (!date) return '-';
    return date.toLocaleString();
  };

  // 处理成员状态变更
  const handleStatusChange = async (record, newStatus) => {
    try {
      const statusText = newStatus === 1 ? '启用' : '禁用';
      
      // 检查企业微信状态，只有企业微信状态为已激活的成员才能启用
      if (newStatus === 1 && record.status !== 1) {
        message.error('只能启用企业微信状态为"已激活"的成员');
        return;
      }
      
      Modal.confirm({
        title: `确认${statusText}成员`,
        content: `确定要${statusText}成员"${record.name}"吗？${newStatus === 1 ? '只有企业微信状态为"已激活"的成员才能在系统中启用。' : ''}`,
        okText: '确认',
        cancelText: '取消',
        icon: <ExclamationCircleOutlined />,
        onOk: async () => {
          message.loading(`正在${statusText}成员...`, 0);
          
          try {
            const response = await api.post(`/wework/employees/${record.id}/status?status=${newStatus}`);
            
            message.destroy();
            
            if (response.data && response.data.success) {
              message.success(`成功${statusText}成员`);
              
              // 更新列表中的状态
              const newEmployees = employees.map(emp => {
                if (emp.id === record.id) {
                  return {
                    ...emp,
                    systemStatus: newStatus
                  };
                }
                return emp;
              });
              
              setEmployees(newEmployees);
              
              // 如果抽屉已打开并显示该成员，也更新抽屉中的状态
              if (drawerVisible && drawerEmployee && drawerEmployee.id === record.id) {
                setDrawerEmployee({
                  ...drawerEmployee,
                  systemStatus: newStatus
                });
              }
            } else {
              // 处理后端返回的错误信息
              const errorMsg = response.data?.message || '未知错误';
              if (errorMsg.includes('最大企微用户数限制') || errorMsg.includes('用户数限制')) {
                // 用户数量限制错误，显示更友好的提示
                Modal.error({
                  title: '操作失败 - 已达到用户数量限制',
                  content: (
                    <div>
                      <p>{errorMsg}</p>
                      <p>请联系管理员升级您的套餐，或停用其他不需要的用户后再尝试。</p>
                    </div>
                  ),
                });
              } else {
                // 其他错误
                message.error('操作失败: ' + errorMsg);
              }
            }
          } catch (innerError) {
            message.destroy();
            console.error('更新成员状态时发生错误:', innerError);
            
            // 处理响应错误
            if (innerError.response?.data) {
              const errorMsg = innerError.response.data.message || '未知错误';
              if (errorMsg.includes('最大企微用户数限制') || errorMsg.includes('用户数限制')) {
                // 用户数量限制错误，显示更友好的提示
                Modal.error({
                  title: '操作失败 - 已达到用户数量限制',
                  content: (
                    <div>
                      <p>{errorMsg}</p>
                      <p>请联系管理员升级您的套餐，或停用其他不需要的用户后再尝试。</p>
                    </div>
                  ),
                });
              } else {
                message.error('更新成员状态失败: ' + errorMsg);
              }
            } else {
              // 网络错误或其他未知错误
              message.error('更新成员状态失败: ' + innerError.message);
            }
          }
        }
      });
    } catch (error) {
      console.error('更新成员状态失败:', error);
      message.error('更新成员状态失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <Space>
          <a onClick={() => getEmployeeDetail(record.id)}>
            {text}
          </a>
        </Space>
      ),
    },
    {
      title: '别名',
      dataIndex: 'alias',
      key: 'alias',
    },
    {
      title: '部门',
      dataIndex: 'departments',
      key: 'departments',
      render: (departments, record) => {
        if (!departments || departments.length === 0) {
          return <Text type="secondary">无部门</Text>;
        }
        
        // 尝试解析部门数据
        let deptIds = [];
        try {
          if (typeof departments === 'string') {
            deptIds = JSON.parse(departments);
          } else if (Array.isArray(departments)) {
            deptIds = departments;
          }
        } catch (err) {
          console.error('解析部门数据失败:', err);
          return <Text type="secondary">数据格式错误</Text>;
        }
        
        if (deptIds.length === 0) {
          return <Text type="secondary">无部门</Text>;
        }
        
        return (
          <Space size={[0, 4]} wrap>
            {deptIds.slice(0, 2).map(deptId => (
              <Tag 
                key={deptId} 
                color="blue"
                icon={<TeamOutlined />}
              >
                {getDepartmentName(deptId)}
              </Tag>
            ))}
            {deptIds.length > 2 && (
              <Tooltip title={deptIds.slice(2).map(deptId => getDepartmentName(deptId)).join(', ')}>
                <Tag>+{deptIds.length - 2}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: '职位',
      dataIndex: 'position',
      key: 'position',
      ellipsis: true,
    },
    // {
    //   title: '手机',
    //   dataIndex: 'mobile',
    //   key: 'mobile',
    //   render: (text) => text ? (
    //     <Space>
    //       <PhoneOutlined />
    //       {text}
    //     </Space>
    //   ) : '-'
    // },
    // {
    //   title: '邮箱',
    //   dataIndex: 'email',
    //   key: 'email',
    //   ellipsis: true,
    //   render: (text) => text ? (
    //     <Space>
    //       <MailOutlined />
    //       <Text ellipsis>{text}</Text>
    //     </Space>
    //   ) : '-'
    // },
    {
      title: '企微状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let statusText = '未知';
        let color = 'default';
        
        switch (status) {
          case 1:
            statusText = '已激活';
            color = 'green';
            break;
          case 2:
            statusText = '已禁用';
            color = 'red';
            break;
          case 4:
            statusText = '未激活';
            color = 'orange';
            break;
          case 5:
            statusText = '退出企业';
            color = 'red';
            break;
          default:
            break;
        }
        
        return <Tag color={color}>{statusText}</Tag>;
      },
    },
    {
      title: '系统状态',
      dataIndex: 'systemStatus',
      key: 'systemStatus',
      render: (systemStatus) => {
        const statusText = systemStatus === 1 ? '已启用' : '已禁用';
        const color = systemStatus === 1 ? 'green' : 'red';
        
        return <Tag color={color}>{statusText}</Tag>;
      },
    },
    {
      title: '互通许可',
      key: 'externalLicense',
      render: (_, record) => {
        const has = record.hasExternalLicense;
        const expireDate = record.externalExpireAt ? new Date(record.externalExpireAt) : null;
        const nowMs = Date.now();
        const isExpired = has && expireDate && expireDate.getTime() <= nowMs;
        const isValid = has && expireDate && expireDate.getTime() > nowMs;
        const tagColor = isValid ? 'green' : (isExpired ? 'red' : (has ? 'default' : 'default'));
        const tagText = isValid ? '已开通' : (isExpired ? '已过期' : (has ? '已开通' : '未开通'));
        const expire = expireDate ? expireDate.toLocaleString() : '-';
        return (
          <Space direction="vertical" size={0}>
            <Tag color={tagColor}>
              {tagText}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>到期：{expire}</Text>
          </Space>
        );
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="link" 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              getEmployeeDetail(record.id);
            }}
          >
            详情
          </Button>
          
          {record.systemStatus === 1 ? (
            <Button 
              type="link" 
              danger
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleStatusChange(record, 0);
              }}
            >
              停用
            </Button>
          ) : (
            <Button 
              type="link" 
              size="small"
              disabled={record.status !== 1}
              onClick={(e) => {
                e.stopPropagation();
                handleStatusChange(record, 1);
              }}
            >
              启用
            </Button>
          )}
        </Space>
      ),
    },
  ];


  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>企业微信成员列表</Title>
        </Col>
      </Row>

      <Alert
        message="状态说明"
        description={
          <div style={{ lineHeight: '1.4' }}>
            <p style={{ margin: '2px 0' }}>• <strong>企微状态</strong>：由企业微信控制的成员在企业微信中的状态，<strong>只有"已激活"状态的成员才能在系统中启用</strong></p>
            <p style={{ margin: '2px 0' }}>• <strong>系统状态</strong>：控制成员在本系统内的权限，只有"已启用"的成员才能使用系统功能</p>
            <p style={{ margin: '2px 0' }}>• <strong>试用/许可</strong>：试用期或未到拦截时间内，启用成员可不占用许可；试用结束且开启拦截后，启用成员将自动分配或从停用成员自动转移许可</p>
            <p style={{ margin: '2px 0' }}>• <strong>注意</strong>：转移许可的成员为在职成员且在服务商应用的可见范围内时，转移后30天后才可进行下次转移</p>
          </div>
        }
        type="warning"
        showIcon
        style={{ marginBottom: '12px', padding: '10px 24px', border: 'none'  }}
      />

      {renderTrialAlert()}

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Input.Search
                  placeholder="搜索成员姓名/账号"
                  value={searchKeyword}
                  onChange={e => setSearchKeyword(e.target.value)}
                  onSearch={handleSearch}
                  enterButton
                  size="middle"
                  style={{ height: 30, lineHeight: '30px', width: 220 }}
                />
                <TreeSelect
                  placeholder="按部门筛选"
                  style={{ width: 200, height: 30, verticalAlign: 'middle' }}
                  value={selectedDepartment}
                  onChange={(value) => {
                    setCurrentPage(0);
                    setSelectedDepartment(value);
                  }}
                  allowClear
                  size="middle"
                  dropdownStyle={{ minWidth: 200 }}
                  treeData={convertDepartmentsToTreeData(departments)}
                  showSearch
                  treeNodeFilterProp="title"
                  treeLine
                />
              </Space>
            </Col>
          </Row>
          <Row>
            <Col>
              <Space>
                <Button 
                  type="primary" 
                  icon={<SyncOutlined spin={syncing} />} 
                  onClick={handleSync}
                  loading={syncing}
                >
                  同步企业微信成员
                </Button>
                {/* 许可统计与其他同步操作已合并至“同步企业微信成员” */}
              </Space>
            </Col>
          </Row>
        </div>
        
        <Table
          rowKey="id"
          columns={columns}
          dataSource={employees}
          loading={loading}
          pagination={false}
          onRow={(record) => ({
            onClick: () => getEmployeeDetail(record.id),
            style: { cursor: 'pointer' }
          })}
        />
        
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={currentPage + 1}
            pageSize={pageSize}
            total={totalElements}
            showTotal={(total) => `共 ${total} 条`}
            onChange={handlePageChange}
            onShowSizeChange={handlePageSizeChange}
            showSizeChanger
            showQuickJumper
          />
        </div>
      </Card>

      {/* 许可统计弹窗已移除 */}

      {/* 成员详情抽屉 */}
      <Drawer
        title={drawerEmployee ? `${drawerEmployee.name} 的详细信息` : '成员详情'}
        width={700}
        open={drawerVisible}
        onClose={handleDrawerClose}
        destroyOnClose
        extra={
          drawerEmployee && (
            <Space>
              {drawerEmployee.systemStatus === 1 ? (
                <Button 
                  danger
                  onClick={() => handleStatusChange(drawerEmployee, 0)}
                >
                  停用成员
                </Button>
              ) : (
                <Button 
                  type="primary"
                  disabled={drawerEmployee.status !== 1}
                  onClick={() => handleStatusChange(drawerEmployee, 1)}
                >
                  启用成员
                </Button>
              )}
            </Space>
          )
        }
      >
        {drawerLoading ? (
          <Spin tip="加载中...">
            <div style={{ height: 400 }}></div>
          </Spin>
        ) : drawerEmployee ? (
          <>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Avatar 
                  size={100} 
                  icon={<UserOutlined />}
                  src={drawerEmployee.avatar} 
                />
              </Col>
              <Col span={18}>
                <Title level={4}>{drawerEmployee.name}</Title>
                {drawerEmployee.position && (
                  <Paragraph>
                    <Space>
                      <IdcardOutlined />
                      {drawerEmployee.position}
                    </Space>
                  </Paragraph>
                )}
                <Space>
                  <Tag color={
                    drawerEmployee.status === 1 ? 'green' :
                    drawerEmployee.status === 2 ? 'red' :
                    drawerEmployee.status === 4 ? 'orange' :
                    drawerEmployee.status === 5 ? 'red' : 'default'
                  }>
                    企微状态: {
                      drawerEmployee.status === 1 ? '已激活' :
                      drawerEmployee.status === 2 ? '已禁用' :
                      drawerEmployee.status === 4 ? '未激活' :
                      drawerEmployee.status === 5 ? '退出企业' : '未知状态'
                    }
                  </Tag>
                  <Tag color={drawerEmployee.systemStatus === 1 ? 'green' : 'red'}>
                    系统状态: {drawerEmployee.systemStatus === 1 ? '已启用' : '已禁用'}
                  </Tag>
                </Space>
              </Col>
            </Row>

            <Divider />

            <Descriptions title="基本信息" bordered column={1} size="small">
              <Descriptions.Item label="企微账号">{drawerEmployee.userid || '-'}</Descriptions.Item>
              {drawerEmployee.alias && <Descriptions.Item label="别名">{drawerEmployee.alias}</Descriptions.Item>}
              {drawerEmployee.mobile && (
                <Descriptions.Item label="手机">
                  <Space>
                    <PhoneOutlined />
                    {drawerEmployee.mobile}
                  </Space>
                </Descriptions.Item>
              )}
              {drawerEmployee.email && (
                <Descriptions.Item label="邮箱">
                  <Space>
                    <MailOutlined />
                    {drawerEmployee.email}
                  </Space>
                </Descriptions.Item>
              )}
              {drawerEmployee.bizMail && (
                <Descriptions.Item label="企业邮箱">
                  <Space>
                    <MailOutlined />
                    {drawerEmployee.bizMail}
                  </Space>
                </Descriptions.Item>
              )}
              {drawerEmployee.telephone && (
                <Descriptions.Item label="座机">
                  <Space>
                    <PhoneOutlined />
                    {drawerEmployee.telephone}
                  </Space>
                </Descriptions.Item>
              )}
              {drawerEmployee.address && (
                <Descriptions.Item label="地址">
                  <Space>
                    <EnvironmentOutlined />
                    {drawerEmployee.address}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <Descriptions title="部门信息" bordered column={1} size="small">
              {drawerEmployee.departments && (
                <Descriptions.Item label="所在部门">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {(() => {
                      let deptIds = [];
                      try {
                        if (typeof drawerEmployee.departments === 'string') {
                          deptIds = JSON.parse(drawerEmployee.departments);
                        } else if (Array.isArray(drawerEmployee.departments)) {
                          deptIds = drawerEmployee.departments;
                        }
                      } catch (err) {
                        console.error('解析部门数据失败:', err);
                        return <Text type="danger">数据格式错误</Text>;
                      }

                      return deptIds.length > 0 ? (
                        deptIds.map(deptId => (
                          <Tag 
                            key={deptId} 
                            color="blue"
                            icon={<TeamOutlined />}
                            style={{ margin: '2px 0' }}
                          >
                            {getDepartmentName(deptId)}
                          </Tag>
                        ))
                      ) : <Text type="secondary">无部门信息</Text>;
                    })()}
                  </Space>
                </Descriptions.Item>
              )}
              {drawerEmployee.mainDepartment && (
                <Descriptions.Item label="主部门">
                  <Tag color="green" icon={<TeamOutlined />}>
                    {getDepartmentName(drawerEmployee.mainDepartment)}
                  </Tag>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />
            
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">创建时间:</Text>
                <Paragraph>
                  {formatDateTime(drawerEmployee.createdAt) || '-'}
                </Paragraph>
              </Col>
              <Col span={12}>
                <Text type="secondary">最后更新:</Text>
                <Paragraph>
                  {formatDateTime(drawerEmployee.updatedAt) || '-'}
                </Paragraph>
              </Col>
            </Row>
          </>
        ) : (
          <Empty description="未找到成员信息" />
        )}
      </Drawer>
    </div>
  );
};

export default WeworkEmployees; 